import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import Section from "@/pages/settings/components/sections/Section";
import AddActionButton from "@/components/ui/add-action-button";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { Close, OnboardingBanner } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { Switch } from "@/components/ui/switch";
import SettingItemLayout from "@/pages/settings/components/settingsItems/SettingItemLayout";
import DraggableStagesList from "@/pages/settings/components/shared/stageStudio/DraggableStagesList";
import { calculateNewStageOrder } from "@/pages/settings/components/shared/stageStudio/calculateNewStageOrder";
import {
  useOnboardingStages,
  useCreateOnboardingStage,
  useUpdateOnboardingStagesOrder
} from "@/pages/settings/components/contractsSettings/hooks";
import {
  useAccountSettings,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { resolveBooleanAccountSetting } from "@/entities/settings/accountSettingDefaults";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import { ENABLE_ONBOARDING_PHASE_KEY } from "@/pages/contracts/types";
import OnboardingStageRow from "./components/OnboardingStageRow";
import EmptyOnboardingStages from "./components/EmptyOnboardingStages";
import CreateOnboardingStageModal from "./components/CreateOnboardingStageModal";
import { NewOnboardingStageFormData } from "./types";

const TRANSLATION_KEY = "settings.contracts.onboardingStudio";
const STAGES_QUERY_KEY = ["settings/contracts/stages"];
const BANNER_DISMISSED_STORAGE_KEY = "onboarding-studio-banner-dismissed";

const OnboardingStudioTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: accountSettings } = useAccountSettings();
  const updateAccountSettingByKey = useUpdateAccountSettingByKey();
  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  // Apply the default only once the settings list has loaded. A list without
  // the row means the key is unset. While the list is loading, or if the read
  // failed, the stored value is unknown, so the stage editor stays hidden
  // rather than showing for an account that turned onboarding off.
  const enabled =
    accountSettings !== undefined &&
    resolveBooleanAccountSetting(
      ENABLE_ONBOARDING_PHASE_KEY,
      keyValuePairs[ENABLE_ONBOARDING_PHASE_KEY]
    );

  const { data: stagesData, isLoading, refetch } = useOnboardingStages();
  const stages = stagesData?.stages || [];

  const createStage = useCreateOnboardingStage();
  const updateStagesOrder = useUpdateOnboardingStagesOrder();

  const [localStages, setLocalStages] = useState<OnboardingStage[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(
    () => sessionStorage.getItem(BANNER_DISMISSED_STORAGE_KEY) !== "true"
  );

  const handleDismissBanner = () => {
    sessionStorage.setItem(BANNER_DISMISSED_STORAGE_KEY, "true");
    setShowBanner(false);
  };

  useEffect(() => {
    setLocalStages([...stages]);
  }, [stages]);

  const handleToggle = (next: boolean) => {
    updateAccountSettingByKey.mutate(
      {
        pathParams: { settingKey: ENABLE_ONBOARDING_PHASE_KEY },
        data: { value: next }
      },
      {
        onError: () => toast.error(t(`${TRANSLATION_KEY}.toggleError`))
      }
    );
  };

  const handleCreate = (data: NewOnboardingStageFormData) => {
    createStage.mutate(
      {
        data: {
          name: data.name,
          stageColor: data.color,
          description: data.description ?? null
        }
      },
      {
        onSuccess: (newStage) => {
          if (!newStage?.id) return;
          const newOrder = calculateNewStageOrder(
            stages,
            newStage.id,
            data.positionInPipeline
          );
          updateStagesOrder.mutate({ data: newOrder });
          refetch();
          toast.success(
            <ToastBody
              title={t(`${TRANSLATION_KEY}.createStage.successToast.title`, {
                stageName: data.name
              })}
              subtitle={t(
                `${TRANSLATION_KEY}.createStage.successToast.subtitle`
              )}
            />,
            { duration: 5000 }
          );
          navigate(
            `${location.pathname}/stages/${newStage.id}${location.search}`
          );
        },
        onError: () =>
          toast.error(t(`${TRANSLATION_KEY}.createStage.errorToast`))
      }
    );
  };

  return (
    <div className="flex flex-col w-full gap-12">
      {showBanner && (
        <div
          className="relative flex gap-6 rounded-2xl border border-border-widget-card overflow-hidden"
          style={{
            background:
              "linear-gradient(0deg, rgba(51, 102, 255, 0.04) 0%, rgba(51, 102, 255, 0.04) 100%), var(--surface-widget-card, #161E2C)"
          }}
          data-test="onboarding-banner"
        >
          <div className="flex flex-col gap-4 p-6 max-w-[26rem]">
            <Text variant="lgBody">{t(`${TRANSLATION_KEY}.banner.title`)}</Text>
            <Text variant="bodySecondary">
              {t(`${TRANSLATION_KEY}.banner.description`)}
            </Text>
          </div>
          <div className="flex-1 flex items-end justify-end pr-12 [&>svg]:max-h-[140px] [&>svg]:object-contain">
            <OnboardingBanner />
          </div>
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            className="absolute top-3 right-3"
            onClick={handleDismissBanner}
            aria-label={t(`${TRANSLATION_KEY}.banner.dismiss`)}
            data-test="onboarding-banner-dismiss"
          >
            <Close />
          </IconButton>
        </div>
      )}

      <div className="rounded-2xl border border-border-border1 bg-surface-elevation1 overflow-hidden">
        <SettingItemLayout
          title={t(`${TRANSLATION_KEY}.enableToggle.title`)}
          subTitle={t(`${TRANSLATION_KEY}.enableToggle.subtitle`)}
          isLastItem
          customAction={
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              size="lg"
              data-test="onboarding-enable-toggle"
            />
          }
        />
      </div>

      {enabled && (
        <Section
          title={t(`${TRANSLATION_KEY}.stages.title`)}
          subTitle={t(`${TRANSLATION_KEY}.stages.subtitle`)}
          action={
            stages.length > 0 ? (
              <AddActionButton
                label={t(`${TRANSLATION_KEY}.stages.create`)}
                onClick={() => setIsCreateOpen(true)}
                disabled={isLoading}
                data-test="onboarding-create-stage-button"
              />
            ) : undefined
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : localStages.length > 0 ? (
            <DraggableStagesList<OnboardingStage>
              items={localStages}
              onItemsChange={setLocalStages}
              tPrefix={`${TRANSLATION_KEY}.stages`}
              queryKey={STAGES_QUERY_KEY}
              persistOrder={(orderedIds) =>
                updateStagesOrder.mutateAsync({ data: orderedIds })
              }
              droppableId="onboarding-stages-list"
              renderRow={({ item, index, isDragDisabled, isLast }) => (
                <OnboardingStageRow
                  key={item.id}
                  item={item}
                  index={index}
                  isDragDisabled={isDragDisabled}
                  isLast={isLast}
                />
              )}
            />
          ) : (
            <EmptyOnboardingStages onCreateClick={() => setIsCreateOpen(true)} />
          )}
        </Section>
      )}

      {enabled && (
        <CreateOnboardingStageModal
          isOpen={isCreateOpen}
          setIsOpen={setIsCreateOpen}
          existingStages={stages}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

export default OnboardingStudioTab;
